import { create } from 'zustand';

import { cn } from '@/lib/utils';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { buttonVariants } from './button';

interface DialogOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

interface DialogState {
  open: boolean;
  mode: 'confirm' | 'alert';
  options: DialogOptions;
  resolve: ((value: boolean) => void) | null;
}

const useDialogStore = create<DialogState>(() => ({
  open: false,
  mode: 'confirm',
  options: { title: '', description: '' },
  resolve: null,
}));

function handleClose(result: boolean) {
  const { resolve } = useDialogStore.getState();
  resolve?.(result);
  useDialogStore.setState({ open: false, resolve: null });
}

export function showConfirm(options: DialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    useDialogStore.setState({
      open: true,
      mode: 'confirm',
      options,
      resolve,
    });
  });
}

export function showAlert(options: Omit<DialogOptions, 'cancelText' | 'variant'>): Promise<void> {
  return new Promise<void>((resolve) => {
    useDialogStore.setState({
      open: true,
      mode: 'alert',
      options: { ...options, variant: 'default' },
      resolve: () => resolve(),
    });
  });
}

export function ConfirmDialog() {
  const { open, mode, options } = useDialogStore();

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && handleClose(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{options.title}</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {options.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {mode === 'confirm' && (
            <AlertDialogCancel onClick={() => handleClose(false)}>
              {options.cancelText ?? '취소'}
            </AlertDialogCancel>
          )}
          <AlertDialogAction
            className={cn(
              options.variant === 'destructive' && buttonVariants({ variant: 'destructive' }),
            )}
            onClick={() => handleClose(true)}
          >
            {options.confirmText ?? '확인'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
